import { Component, Inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AccountResponse, ManagementAccountControllerService } from 'api-auth';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { UtilsService } from '../../../../shared/services/utils.service';
import { Constants } from '../../../../shared/interfaces/constants';
import { take } from 'rxjs/internal/operators/take';
import { SharedService } from '../../../../shared/services/shared.service';
import { environment } from '../../../../../environments/environment';

interface InputData {
	mode: 'NEW' | 'EDIT' | 'DEACTIVATE' | 'ACTIVATE' | 'PENDING' | 'DELETE';
	type: string;
	item: AccountResponse;
}

@Component({
	selector: 'app-manage-user',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		TranslateModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		MatDialogModule,
	],
	templateUrl: './manage-user.component.html',
	styleUrl: './manage-user.component.scss',
})
export class ManageUserComponent {
	scopes = Constants.SCOPES['MGMT'];
  reseller = environment.reseller;

	formGroup: FormGroup = new FormGroup({});
	passwordVisible = false;

	apiLoading = {
		management: false,
	};

	constructor(
		public dialogRef: MatDialogRef<ManageUserComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiManagementAccount: ManagementAccountControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		switch (this.data.mode) {
			case 'NEW':
			case 'EDIT': {
				this.formGroup = this.fb.group({
					identifier: [, [Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('E_MAIL') || '')]],
					password: [],
					name: [, [Validators.required]],
					email: [],
					clientId: [],
					role: ['MGMT'],
					scopes: [, [Validators.required]],
				});

				if (this.data.item) {
					this.formGroup.patchValue(this.data.item);
					this.formGroup.controls['identifier'].disable();
					// this.formGroup.controls['password'].removeValidators(Validators.required);
				}
				break;
			}
		}
	}

	action() {
		this.apiLoading.management = true;
		switch (this.data.mode) {
			case 'NEW': {
				this.createAccount();
				break;
			}
			case 'EDIT': {
				this.editAccount();
				break;
			}
			case 'DEACTIVATE': {
				this.deactivateAccount();
				break;
			}
			case 'ACTIVATE': {
				this.activateAccount();
				break;
			}
			case 'DELETE': {
				this.deleteAccount();
				break;
			}
		}
	}
	closeDialog(result: boolean) {
		this.dialogRef.close(result);
	}

	private createAccount() {
		this.formGroup.controls['email'].setValue(this.formGroup.controls['identifier'].value);
		this.apiManagementAccount
			.createAccount({...this.utils.validateRequestObject(this.formGroup.value), reseller: this.reseller.name})
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private editAccount() {
		this.apiManagementAccount
			.updateAccount(this.utils.validateRequestObject(this.formGroup.value), this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private deactivateAccount() {
		this.apiManagementAccount
			.deactivateAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DEACTIVATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private activateAccount() {
		this.apiManagementAccount
			.activateAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTIVATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private deleteAccount() {
		this.apiManagementAccount
			.deleteAccount(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
					this.closeDialog(true);
				},
				error: err => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}
}
